// src/components/LessonUploader.js
import React, { useState, useRef } from 'react';
import { uploadAPI, documentAPI } from '../services/api';
import { trackUserEvent } from '../utils/telemetry';

// --- HEX CODES FOR INLINE STYLES ---
const BRAND = {
    charcoal: '#2D4042',
    teal: '#75B9BE',
    danger: '#E07A5F'
};

const DEFAULT_EXTRACTION_MODEL = 'gemini-2.5-flash';
const UPLOAD_ERROR_MESSAGE = 'Upload failed. Please try again or contact support if the problem continues.';

const LessonUploader = ({
  bucketTitle,
  bucketTag,
  lessonPlanId,
  onUploadComplete,
  uploadedDocuments = [],
  isDemoMode,
  actionButton = null,
  showActionProgress = false,
  singleDocumentOnly = false,
  singleDocumentLimitReached = false
}) => {
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  
  const fileInputRef = useRef(null);

  const handleIncomingFiles = (incomingFiles = []) => {
    const selectedFiles = Array.from(incomingFiles || []);

    setFiles((prevFiles) => {
      const existingFiles = Array.isArray(prevFiles) ? prevFiles : [];
      const mergedFiles = [...existingFiles, ...selectedFiles];

      // Deduplicate by file identity tuple so repeated drops don't duplicate the queue.
      const dedupedFiles = mergedFiles.filter((file, index, arr) => {
        const identity = `${file.name}::${file.size}::${file.lastModified}`;
        return arr.findIndex((candidate) => `${candidate.name}::${candidate.size}::${candidate.lastModified}` === identity) === index;
      });

      return singleDocumentOnly ? dedupedFiles.slice(0, 1) : dedupedFiles;
    });

    setStatus('');
  };

  const handleFileSelect = (e) => {
    handleIncomingFiles(e.target.files || []);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (loading || singleDocumentLimitReached) return;

    handleIncomingFiles(e.dataTransfer?.files || []);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading || singleDocumentLimitReached) return;
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleUploadClick = () => {
    if (files.length === 0) {
      fileInputRef.current.click();
    } else {
      performUpload(DEFAULT_EXTRACTION_MODEL);
    }
  };

  const performUpload = async (model) => {
    setLoading(true);
    setStatus('Uploading...');

    // Telemetry should never block uploads.
    void trackUserEvent('UPLOAD', 'lesson_upload_initiated', {
      extractionModel: model,
      fileCount: files.length,
      lessonPlanId,
      lessonTag: bucketTag,
      isDemoMode: Boolean(isDemoMode),
    });

    const formData = new FormData();
    const filesToUpload = singleDocumentOnly ? files.slice(0, 1) : files;
    filesToUpload.forEach(file => {
      formData.append('files', file);
    });
    formData.append('lessonPlanId', lessonPlanId);
    formData.append('lessonTag', bucketTag);
    formData.append('model', model); 

    try {
      const res = await uploadAPI.uploadFiles(formData);
      setStatus(res.data.message);
      setFiles([]);
      onUploadComplete();
    } catch (err) {
      console.error('Upload failed:', err);
      setStatus(UPLOAD_ERROR_MESSAGE);
    } finally {
        setLoading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (window.confirm('Are you sure you want to permanently delete this document and its data?')) {
        try {
            await documentAPI.deleteById(documentId);
            onUploadComplete(); 
        } catch (error) {
            alert('Failed to delete the document.');
        }
    }
  };

  const getSelectedFilesText = () => {
      if (files.length === 0) return 'No files selected';
      if (files.length === 1) return files[0].name;
      const names = files.map(f => f.name).join(', ');
      return `${files.length} files: ${names.length > 30 ? names.substring(0, 30) + '...' : names}`;
  };

  const hasProcessingDocuments = uploadedDocuments.some((doc) =>
    ['UPLOADED', 'EXTRACTING', 'PROCESSING'].includes(doc.status)
  );

  return (
    <div className="dash-card" style={{ padding: '20px', marginBottom: '25px', background: 'white' }}>
      <style>
        {`
          .lesson-action-progress-container {
            width: 100%;
            height: 8px;
            background-color: #eee;
            border-radius: 4px;
            overflow: hidden;
            margin-top: 12px;
          }
          .lesson-action-progress-fill {
            height: 100%;
            width: 60%;
            border-radius: 4px;
            background-color: #75B9BE;
            animation: lessonActionPulse 1.5s infinite ease-in-out;
          }
          @keyframes lessonActionPulse {
            0% { width: 40%; transform: translateX(-10%); }
            50% { width: 70%; transform: translateX(20%); }
            100% { width: 40%; transform: translateX(100%); }
          }
          .processing-indicator-dot {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background-color: #2E7D32;
            box-shadow: 0 0 0 0 rgba(46, 125, 50, 0.55);
            animation: processingDotPulse 1.2s infinite ease-in-out;
          }
          @keyframes processingDotPulse {
            0% { transform: scale(0.9); box-shadow: 0 0 0 0 rgba(46, 125, 50, 0.55); }
            70% { transform: scale(1.1); box-shadow: 0 0 0 8px rgba(46, 125, 50, 0); }
            100% { transform: scale(0.9); box-shadow: 0 0 0 0 rgba(46, 125, 50, 0); }
          }
        `}
      </style>
      
      <h4 style={{
          marginTop: 0, 
          marginBottom: '15px', 
          color: BRAND.charcoal, 
          textTransform: 'uppercase', 
          fontSize: '0.85em', 
          letterSpacing: '1px', 
          fontWeight: '800'
      }}>
          {bucketTitle}
      </h4>
      
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{ 
          marginBottom: '15px', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '15px', 
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          border: `2px dashed ${BRAND.teal}`,
          borderRadius: '12px',
          padding: '8px',
          background: isDragOver ? '#DDF7F2' : '#F0FDFA',
          transition: 'all 0.15s ease'
      }}>
        <div style={{ width: '100%', marginBottom: '2px' }}>
          <span style={{
            fontSize: '0.78rem',
            fontWeight: '700',
            color: isDragOver ? BRAND.teal : '#6B7280',
            letterSpacing: '0.2px'
          }}>
            📥 Drag & drop files here, or use “Select Documents”
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
        <input 
            type="file" 
            multiple={!singleDocumentOnly}
            ref={fileInputRef}
            onChange={handleFileSelect} 
            disabled={loading || singleDocumentLimitReached}
            style={{ display: 'none' }} 
        />
        
        <button 
            className="btn-base"
            onClick={() => fileInputRef.current.click()}
            disabled={loading || singleDocumentLimitReached}
            style={{
                background: '#F0F2F5',
                color: BRAND.charcoal,
                border: `2px solid ${BRAND.charcoal}`,
                borderBottom: `4px solid ${BRAND.charcoal}`
            }}
        >
            📄 Select Documents
        </button>

        {singleDocumentLimitReached && (
          <span style={{ fontSize: '0.82rem', color: BRAND.danger, fontWeight: '700' }}>
            Single document limit reached. Delete current document to upload a replacement.
          </span>
        )}

        {files.length > 0 && (
            <button 
                className="btn-base"
                onClick={handleUploadClick}
                disabled={loading}
                style={{
                    background: BRAND.teal,
                    color: 'white'
                }}
            >
                {loading ? 'Uploading...' : 'Start Upload'}
            </button>
        )}

        <span style={{ fontSize: '0.9rem', color: '#666', fontWeight: '500', marginLeft: '5px' }}>
            {getSelectedFilesText()}
        </span>
        </div>

        {actionButton && (
            <button
                className="btn-base btn-coral"
                onClick={actionButton.onClick}
                disabled={loading || hasProcessingDocuments || actionButton.disabled}
                style={{ whiteSpace: 'nowrap' }}
                title={hasProcessingDocuments ? 'Please wait for file processing to complete.' : (actionButton.title || '')}
            >
                {actionButton.label}
            </button>
        )}
      </div>

      {showActionProgress && (
        <div className="lesson-action-progress-container" aria-label="Lesson generation in progress">
          <div className="lesson-action-progress-fill"></div>
        </div>
      )}

      {status && (
        <p style={{ color: status.includes('failed') ? BRAND.danger : 'green', marginTop: '5px', marginBottom:'10px', fontSize: '0.9rem', fontWeight:'600', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <span>{status}</span>
          {hasProcessingDocuments && /files?\s+uploaded/i.test(status) && (
            <span className="processing-indicator-dot" aria-label="Files are being processed" />
          )}
        </p>
      )}

      {uploadedDocuments.length > 0 && (
        <div style={{ marginTop: '15px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85em' }}>
            <thead style={{ borderBottom: `2px solid ${BRAND.charcoal}` }}>
              <tr style={{ textAlign: 'left' }}>
                <th style={{ padding: '8px', color: BRAND.charcoal, fontWeight:'800' }}>Document Name</th>
                <th style={{ padding: '8px', color: BRAND.charcoal, fontWeight:'800' }}>Status</th>
                <th style={{ padding: '8px', textAlign: 'right', color: BRAND.charcoal, fontWeight:'800' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {uploadedDocuments.map(doc => (
                <tr key={doc.document_id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '10px 8px', fontWeight:'500' }}>{doc.filename}</td>
                  <td style={{ padding: '10px 8px' }}>
                    <span style={{ 
                        fontWeight: '800', 
                        fontSize: '0.8rem',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        backgroundColor: doc.status === 'COMPLETED' ? '#E8F5E9' : (doc.status === 'FAILED' ? '#FFEBEE' : '#FFF3E0'),
                        color: doc.status === 'COMPLETED' ? '#2E7D32' : (doc.status === 'FAILED' ? '#C62828' : '#EF6C00'),
                        border: '1px solid rgba(0,0,0,0.05)'
                    }}>
                      {doc.status}
                    </span>
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                    <button 
                        onClick={() => handleDelete(doc.document_id)} 
                        className="btn-base"
                        style={{ 
                            backgroundColor: 'white', 
                            color: BRAND.danger, 
                            border: `2px solid ${BRAND.danger}`,
                            padding: '4px 10px', 
                            fontSize: '0.8rem',
                            borderBottom: `4px solid ${BRAND.danger}`
                        }}
                    >
                        Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LessonUploader;
